import axios from 'axios'

const baseURL = process.env.VUE_APP_API_URL || '/api/admin'
const REFRESH_SKEW_MS = 30 * 1000

const api = axios.create({
  baseURL,
})

let refreshPromise = null

const decodeJwtPayload = (token) => {
  if (!token) {
    return null
  }

  try {
    const [, payload] = token.split('.')
    if (!payload) {
      return null
    }

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
    const decoded = atob(padded)

    return JSON.parse(decoded)
  } catch (_error) {
    return null
  }
}

const storeTokenExpiry = (accessToken) => {
  const payload = decodeJwtPayload(accessToken)
  const expiresAt = payload?.exp ? new Date(payload.exp * 1000).toISOString() : null

  if (expiresAt) {
    localStorage.setItem('admin_token_expires', expiresAt)
  } else {
    localStorage.removeItem('admin_token_expires')
  }
}

export const clearAdminAuthStorage = () => {
  localStorage.removeItem('admin_token')
  localStorage.removeItem('admin_refresh_token')
  localStorage.removeItem('admin_token_expires')
  localStorage.removeItem('admin_user')
}

export const persistAdminSession = ({ accessToken, refreshToken, adminUser }) => {
  if (accessToken) {
    localStorage.setItem('admin_token', accessToken)
    storeTokenExpiry(accessToken)
  }

  if (refreshToken) {
    localStorage.setItem('admin_refresh_token', refreshToken)
  }

  if (adminUser) {
    localStorage.setItem('admin_user', JSON.stringify(adminUser))
  }
}

const getTokenExpiry = () => {
  const expiresAt = localStorage.getItem('admin_token_expires')
  return expiresAt ? new Date(expiresAt).getTime() : null
}

const isAccessTokenStale = () => {
  const token = localStorage.getItem('admin_token')
  const expiry = getTokenExpiry()

  if (!token || !expiry) {
    return true
  }

  return Date.now() >= (expiry - REFRESH_SKEW_MS)
}

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('admin_refresh_token')

  if (!refreshToken) {
    throw new Error('Missing refresh token')
  }

  if (!refreshPromise) {
    refreshPromise = axios.post(`${baseURL}/auth/refresh`, {
      refresh_token: refreshToken,
    }, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    }).then((response) => {
      const nextAccessToken = response.data?.data?.access_token
      const nextRefreshToken = response.data?.data?.refresh_token || refreshToken

      if (!nextAccessToken) {
        throw new Error('Missing refreshed access token')
      }

      persistAdminSession({
        accessToken: nextAccessToken,
        refreshToken: nextRefreshToken,
        adminUser: JSON.parse(localStorage.getItem('admin_user') || 'null'),
      })

      return nextAccessToken
    }).catch((error) => {
      clearAdminAuthStorage()
      throw error
    }).finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

api.interceptors.request.use(async (config) => {
  config.headers = config.headers || {}

  if (!config.headers.Accept) {
    config.headers.Accept = 'application/json'
  }

  const isAuthRequest = typeof config.url === 'string' && config.url.startsWith('/auth/')

  if (!isAuthRequest && localStorage.getItem('admin_refresh_token') && isAccessTokenStale()) {
    try {
      await refreshAccessToken()
    } catch (_error) {
      // Let the request continue unauthenticated so the backend can return a clear auth error.
    }
  }

  const token = localStorage.getItem('admin_token')
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`
  }

  const method = String(config.method || 'get').toUpperCase()
  const isJsonMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
  const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData

  if (isJsonMutation && !isFormData && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json'
  }

  // Axios DROPS the Content-Type header when a request has no body, so a body-less
  // mutation (e.g. POST /withdrawals/{id}/approve, /complete, /mark-processing)
  // would reach the API without an application/json content type and be rejected
  // with HTTP 415. Send an empty JSON object so the header sticks and the action
  // actually goes through.
  if (isJsonMutation && !isFormData && (config.data === undefined || config.data === null)) {
    config.data = {}
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {}
    const status = error.response?.status
    const isAuthRequest = typeof originalRequest.url === 'string' && originalRequest.url.startsWith('/auth/')

    if (
      status === 401
      && !isAuthRequest
      && !originalRequest._retry
      && localStorage.getItem('admin_refresh_token')
    ) {
      originalRequest._retry = true

      try {
        const nextAccessToken = await refreshAccessToken()
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`

        return api(originalRequest)
      } catch (_refreshError) {
        clearAdminAuthStorage()
      }
    }

    throw error
  }
)

export const hasAdminSession = () => Boolean(
  localStorage.getItem('admin_token') || localStorage.getItem('admin_refresh_token')
)

export default api
